import React, { useCallback, useContext, useEffect, useState } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import { useQueryClient } from '@tanstack/react-query';
import useGetTemplates from './useGetTemplates';
import * as S from './style';
import VotesController from './VotesController';
import { GlobalContext } from '../../context/GlobalState';
import { SCREENS } from '../../constants';
import { errorNoticeDefault } from '../../utils/notice';
import {
  AsyncSelect,
  Badge,
  CustomFormatForm,
  Flex,
  Popup,
  Select,
  TextField,
  ToggleSwitch,
} from '../../components';
import defaultMoodQuestions from '../../config/MoodQuestions';
import {
  createRetrospective,
  updateRetrospective,
} from '../../services/retrospectives';
import { getUsers, getUsersById } from '../../services/users';
import { logEvent, TrackingEvents } from '../../tracking';

const MIN_VOTES_LIMIT = 1;

function formatOptionLabel({ label, isDisabled }) {
  return (
    <Flex align="center" justify="space-between">
      <span>{label}</span>
      {isDisabled && <Badge style={{ marginLeft: 10 }}>PRO</Badge>}
    </Flex>
  );
}

const formatGroupLabel = (data) => (
  <Flex align="center" justify="space-between">
    <span style={{ padding: '14px 4px 0' }}>{data.label}</span>
  </Flex>
);

const mapOptionsSelect = (option) => ({
  label: option.name,
  value: option.id,
  avatarUrl: option.photo_tiny || null,
});

function filterNameBy(textSearch) {
  return (user) => user.name.toLowerCase().includes(textSearch.toLowerCase());
}
const loadUsers = async (textSearch) => {
  const users = await getUsers(textSearch);
  return users
    .filter(filterNameBy(textSearch))
    .map((element) => mapOptionsSelect(element));
};

function isCustomFormat(format) {
  return (
    format &&
    (format.value === 'Custom' ||
      format.id === 'Custom' ||
      format.type === 'Custom')
  );
}

const isFormatValid = (format, customFormat) => {
  return (
    format &&
    format.value &&
    (format.value !== 'Custom' ||
      (isCustomFormat(format) &&
        customFormat &&
        customFormat.length > 0 &&
        customFormat.length <= 5))
  );
};

function FormRetrospective({ open, onClose, editing, duplicating }) {
  const [name, setName] = useState('');
  const [facilitator, setFacilitator] = useState();
  const [participants, setParticipants] = useState([]);
  const [votesLimit, setVotesLimit] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState({});
  const [customColumns, setCustomColumns] = useState(
    editing?.customFormat || duplicating?.customFormat || []
  );
  const [isValid, setIsValid] = useState(false);
  const [isFormReady, setIsFormReady] = useState(false);
  const [haveTeamMood, setHaveTeamMood] = useState(false);
  const [moodQuestions, setMoodQuestions] = useState(defaultMoodQuestions);

  const { isLoading, onLoading, onChangeScreen } = useContext(GlobalContext);

  const queryClient = useQueryClient();

  const {
    data: allFormats,
    isLoading: isLoadingFormats,
    isSuccess: isSuccessLoadedFormats,
  } = useGetTemplates(isFormReady);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const defaultValueFormat =
    isSuccessLoadedFormats && editing
      ? allFormats
          .flatMap((f) => f.options)
          .find((item) => item.value === editing.format) || {
          type: 'Custom',
          columns: editing.customFormat,
        }
      : null;

  useEffect(() => {
    let defaultColumns = [];
    if (editing?.format === 'Custom') {
      defaultColumns = customColumns;
    }

    if (duplicating?.format === 'custom') {
      defaultColumns = customColumns;
    }

    if (isCustomFormat(selectedFormat)) {
      setCustomColumns(selectedFormat.columns || defaultColumns);
    } else if (isCustomFormat(defaultValueFormat)) {
      setCustomColumns(defaultValueFormat.columns || defaultColumns);
    } else if (isCustomFormat(editing)) {
      setCustomColumns(editing.customFormat || defaultColumns);
    } else {
      setCustomColumns([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValueFormat, editing, selectedFormat]);

  const handleVotesLimitToggled = () => {
    if (votesLimit) {
      setVotesLimit(null);
    } else {
      setVotesLimit(5);
    }
  };

  const handleVotesLimitChanged = (increment) => {
    if (votesLimit + increment < MIN_VOTES_LIMIT) return;
    setVotesLimit(increment + votesLimit);
  };

  useEffect(() => {
    const isFacilitatorValid = () => facilitator && facilitator.value;
    const isParticipantsValid = () => participants && participants.length > 0;
    const isNameValid = () => name.length > 0;
    const isMoodValid = () => {
      return (
        !haveTeamMood ||
        (haveTeamMood &&
          moodQuestions &&
          moodQuestions.length > 0 &&
          moodQuestions.length <= 5)
      );
    };

    setIsValid(
      isFacilitatorValid() &&
        isNameValid() &&
        isParticipantsValid() &&
        isFormatValid(selectedFormat, customColumns) &&
        isMoodValid()
      // ((isAzure && team) || !isAzure)
    );
    // }, [name, facilitator, participants, format, team]);
  }, [
    name,
    facilitator,
    participants,
    selectedFormat,
    customColumns,
    moodQuestions,
    haveTeamMood,
  ]);

  useEffect(() => {
    async function setEditingRetrospectiveUsers() {
      let users = await getUsersById([
        editing.facilitator,
        ...editing.participants,
      ]);
      users = users.map((user) => mapOptionsSelect(user));
      setFacilitator(users.find((item) => item.value === editing.facilitator));
      setParticipants(
        users.filter((item) => editing.participants.includes(item.value))
      );
    }

    if (open && editing) {
      (async () => {
        await setEditingRetrospectiveUsers();
        // await setEditingRetrospectiveTeam();
        setName(editing.name);
        setVotesLimit(editing.votesLimit);
        setSelectedFormat(defaultValueFormat || {});
        setIsFormReady(true);
      })();
    }
  }, [defaultValueFormat, editing, open]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const defaultValueFormatByDuplicating =
    isSuccessLoadedFormats && duplicating
      ? allFormats
          .flatMap((f) => f.options)
          .find((item) => item.value === duplicating.format) || {
          type: 'Custom',
          columns: duplicating.customFormat,
        }
      : null;

  useEffect(() => {
    const setDuplicatingRetrospectiveUsers = async () => {
      let users = await getUsersById([
        duplicating.facilitator,
        ...duplicating.participants,
      ]);
      users = users.map((user) => mapOptionsSelect(user));
      setFacilitator(
        users.find((item) => item.value === duplicating.facilitator)
      );
      setParticipants(
        users.filter((item) => duplicating.participants.includes(item.value))
      );
    };

    const loadInfosHandler = async () => {
      await setDuplicatingRetrospectiveUsers();
      setName(duplicating.name);
      setVotesLimit(duplicating.votesLimit);
      setSelectedFormat(
        {
          ...defaultValueFormatByDuplicating,
          columns: duplicating.customFormat,
        } || {}
      );
      if (duplicating?.teamMoodQuestions?.length > 0) {
        setHaveTeamMood(true);
        setMoodQuestions(
          duplicating.teamMoodQuestions.map((mQ, mqIndex) => ({
            id: mqIndex,
            question: mQ,
          }))
        );
      }
      setCustomColumns(duplicating.customFormat);
      setIsFormReady(true);
    };

    if (open && duplicating) {
      loadInfosHandler();
    }
  }, [defaultValueFormatByDuplicating, duplicating, open]);

  useEffect(() => {
    if (editing) {
      setMoodQuestions(editing.teamMoodQuestions);
    }
  }, [editing]);

  useEffect(() => {
    if (open && !duplicating && !editing) {
      setIsFormReady(true);
    }
  }, [duplicating, editing, open]);

  const editingWithTeamMood = () => editing && moodQuestions?.length > 0;

  const handleConfirmClick = async () => {
    onLoading(true);

    const dataFields = {
      name,
      facilitator: facilitator.value,
      participants: participants.map((item) => item.value),
      format: selectedFormat.value,
      votesLimit,
    };

    if (isCustomFormat(selectedFormat)) {
      dataFields.customFormat = customColumns;
    }

    if (editing) {
      logEvent(TrackingEvents.EDIT_RETROSPECTIVE);
      try {
        await updateRetrospective(editing.id, dataFields);
        onClose(true);
      } catch (error) {
        errorNoticeDefault(error);
      }
    } else {
      logEvent(TrackingEvents.CREATE_RETROSPECTIVE, {
        format: dataFields.format,
        participants: dataFields.participants.length,
        teamMoodEnabled: haveTeamMood,
        votesLimitEnabled: !!votesLimit,
      });
      try {
        const retrospectiveId = await createRetrospective(
          dataFields.name,
          dataFields.facilitator,
          dataFields.participants,
          dataFields.format,
          dataFields.customFormat ?? null,
          haveTeamMood ? moodQuestions.map((mQ) => mQ.question) : null,
          votesLimit
          // dataFields.team
        );
        logEvent(TrackingEvents.OPEN_RETROSPECTIVE);
        await onChangeScreen(SCREENS.BOARD, retrospectiveId);
      } catch (error) {
        errorNoticeDefault(error);
      }
    }
    await queryClient.invalidateQueries(['getRetrospectives']);
    onLoading(false);
  };

  const handleDragEndCustomFormat = ({ destination, source }) => {
    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    )
      return;

    const newCF = [...customColumns];

    const [removed] = newCF.splice(source.index, 1);
    newCF.splice(destination.index, 0, removed);

    setCustomColumns(newCF);
  };

  const handleDragTeamMood = ({ destination, source }) => {
    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    )
      return;

    const newMQ = [...moodQuestions];

    const [removed] = newMQ.splice(source.index, 1);
    newMQ.splice(destination.index, 0, removed);

    setMoodQuestions(newMQ);
  };

  const resetForm = useCallback(() => {
    setName();
    setFacilitator();
    setParticipants([]);
    setVotesLimit(null);
    setHaveTeamMood(false);
    setSelectedFormat({});
    setCustomColumns([]);
    setIsFormReady(false);
    setMoodQuestions(defaultMoodQuestions);
  }, []);

  return isFormReady ? (
    <Popup
      open={open}
      title={editing ? 'Edit retrospective' : 'New retrospective'}
      body={
        <>
          <S.Field>
            <TextField
              name="name"
              placeholder="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </S.Field>

          <S.Field>
            <AsyncSelect
              name="facilitator"
              loadOptions={loadUsers}
              defaultValue={facilitator}
              placeholder="Facilitator"
              onChange={setFacilitator}
              cacheOptions
              defaultOptions
            />
          </S.Field>

          <S.Field>
            <AsyncSelect
              name="participants"
              loadOptions={loadUsers}
              defaultValue={participants}
              placeholder="Participants"
              onChange={setParticipants}
              cacheOptions
              defaultOptions
              isMulti
            />
          </S.Field>

          {isSuccessLoadedFormats && (
            <S.Field>
              <Select
                key={defaultValueFormat?.value}
                name="format"
                options={allFormats}
                defaultValue={
                  defaultValueFormat || defaultValueFormatByDuplicating
                }
                placeholder="Select a format"
                onChange={(value) => setSelectedFormat(value)}
                formatOptionLabel={formatOptionLabel}
                formatGroupLabel={formatGroupLabel}
                isDisabled={isLoadingFormats || editing}
              />
            </S.Field>
          )}

          <DragDropContext onDragEnd={handleDragEndCustomFormat}>
            {isCustomFormat(selectedFormat) ? (
              <CustomFormatForm
                editing={editing}
                elements={customColumns}
                setElements={setCustomColumns}
                warning="columns"
                context="customFormat"
                displayField="columnName"
                placeholder="Type a column name..."
              />
            ) : null}
          </DragDropContext>
          <S.Field marginBottom="2px">
            <VotesController
              votesLimit={votesLimit}
              handleVotesLimitToggled={handleVotesLimitToggled}
              handleVotesLimitChanged={handleVotesLimitChanged}
            />
          </S.Field>
          <S.Field marginBottom="2px">
            <ToggleSwitch
              id="team-mood-cb"
              checked={editingWithTeamMood() || haveTeamMood}
              disabled={editing ? 'disabled' : false}
              onChange={() => setHaveTeamMood(!haveTeamMood)}
            >
              Team Mood
            </ToggleSwitch>
          </S.Field>
          {(!editing && haveTeamMood) || editingWithTeamMood() ? (
            <DragDropContext onDragEnd={handleDragTeamMood}>
              <CustomFormatForm
                editing={editing}
                elements={moodQuestions}
                setElements={setMoodQuestions}
                displayField="question"
                warning="questions"
                context="mood"
                placeholder="Type a mood question..."
              />
            </DragDropContext>
          ) : null}
        </>
      }
      cancelLabel="Cancel"
      onCancel={() => {
        resetForm();
        onClose();
      }}
      confirmLabel={editing ? 'Save' : 'New retrospective'}
      onConfirm={handleConfirmClick}
      disabledConfirm={!isValid || isLoading || !isFormReady}
    />
  ) : null;
}

export default FormRetrospective;
